import * as React from 'react'

import { MapPinIcon } from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DeliveryDateLabel from '~/client/src/shared/components/DeliveryDateLabel/DeliveryDateLabel'
import DeliveryFieldIcon from '~/client/src/shared/components/DeliveryFieldIcon/DeliveryFieldIcon'
import DeliveryStatusLabel from '~/client/src/shared/components/DeliveryStatusLabel/DeliveryStatusLabel'
import * as Icons from '~/client/src/shared/components/Icons'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Delivery from '~/client/src/shared/models/Delivery'
import DeliveryCardFieldsConfigStore, {
  ICardAttribute,
  MAX_NAME_LENGTH,
} from '~/client/src/shared/stores/ui/DeliveryCardFieldsConfig.store'
import { truncateText } from '~/client/src/shared/utils/textUtils'

import { getSharedProjectName, SharedInfo } from '../../../models/SharedInfo'
import BaseExpandableWorkflowCard, {
  IBaseWorkflowCardProps,
  workflowCardInjects,
} from '../BaseExpandableWorkflowCard'

interface IProps extends IBaseWorkflowCardProps {
  delivery: Delivery

  sharedInfo?: SharedInfo
  isShared?: boolean
  shouldUseMockValues?: boolean

  onClick?: (delivery: Delivery) => void
}

@inject(...workflowCardInjects)
@observer
export default class DeliveryWorkflowCard extends BaseExpandableWorkflowCard<IProps> {
  private readonly cardFieldsStore: DeliveryCardFieldsConfigStore
  @observable private localShouldUseMockValues: boolean = false

  public constructor(props: IProps) {
    super(props)

    this.localShouldUseMockValues = props.shouldUseMockValues

    this.cardFieldsStore = new DeliveryCardFieldsConfigStore(
      props.delivery,
      props.state,
      props.projectDateStore,
      props.locationAttributesStore,
      props.deliveryVehicleTypesStore,
      props.offloadingEquipmentsStore,
      props.companiesStore,
      props.materialsStore,
      props.projectMembersStore,
      props.materialCategoryStore,
    )
    makeObservable(this)
  }

  @action
  public componentDidUpdate(prevProps: IProps) {
    const { delivery, shouldUseMockValues } = this.props
    this.localShouldUseMockValues = shouldUseMockValues

    if (delivery !== prevProps.delivery) {
      this.cardFieldsStore.init(delivery)
      this.store.resetIsExpanded()
    }
  }

  protected get icon(): JSX.Element {
    if (this.props.delivery.isFromConcreteDirect) {
      return (
        <div className="delivery-concrete-direct-icon row x-center y-center no-flex">
          <Icons.ConcreteDirect className="no-grow" />
        </div>
      )
    }

    return (
      <div className="delivery-workflow-card-icon bg-dark br-rounded text white row x-center y-center no-flex">
        <Icons.DeliveryFilled className="no-grow" />
      </div>
    )
  }

  protected get titleValues(): JSX.Element {
    const { isShared } = this.props

    if (isShared) {
      return (
        <>
          {this.sharedCompanyField}
          {this.sharedDateTimeTitle}
        </>
      )
    }

    return (
      <>
        {this.companyField}
        {this.bookingTimeAndStatusField}
      </>
    )
  }

  private get sharedDateTimeTitle(): JSX.Element {
    return (
      <div className="row x-between mb4">
        <DeliveryDateLabel
          delivery={this.props.delivery}
          className="text-ellipsis mr4"
        />
      </div>
    )
  }

  protected get header(): JSX.Element {
    const { isShared, sharedInfo, projectsStore } = this.props
    if (!isShared || !sharedInfo) {
      return null
    }

    return (
      <div className="row px10 shared-event-background py5 y-center text bold white large shared-event-header">
        <Icons.SharedEntity className="no-grow mr20 shared-entinty-icon" />
        <div>
          {Localization.translator.sharedFrom}
          <span className="text bold white large underline ml5">
            {getSharedProjectName(
              sharedInfo.projectUrl,
              projectsStore,
              sharedInfo.projectFromId,
            )}
          </span>
        </div>
      </div>
    )
  }

  private get sharedCompanyField(): JSX.Element {
    const {
      delivery,
      sharedInfo: { companyName },
    } = this.props

    if (!companyName || !delivery) {
      return null
    }

    return (
      <div className="row y-stretch x-between no-flex-children mb4 overflow-hidden">
        <span className="col row delivery-company-title text extra-large bold ellipsis">
          {Localization.translator.shared}: {companyName}
          <span className="col ml5 text light pa2 pt4">
            ({delivery.bookingId})
          </span>
        </span>
        <DeliveryStatusLabel
          status={delivery.status}
          cancellationReason={delivery.cancellationReason}
          className="col ml8 no-grow"
        />
      </div>
    )
  }

  protected onClick = (ev: React.SyntheticEvent) => {
    ev.stopPropagation()

    const { delivery, onClick } = this.props
    onClick?.(delivery)
  }

  protected get attributeElements(): JSX.Element[] {
    const { isShared, sharedInfo } = this.props

    if (isShared) {
      return sharedInfo?.locationsTo.map((location, idx) => (
        <React.Fragment key={location.locationName + idx}>
          {this.renderSharedLocation(
            location.locationName,
            sharedInfo.locationsTo.length - 1 !== idx,
          )}
        </React.Fragment>
      ))
    }

    const { isFromConcreteDirect } = this.props.delivery

    return this.cardAttributeValues.map((cardAttribute, index) => (
      <React.Fragment key={cardAttribute.fieldId}>
        {this.renderAttributeField(
          {
            ...cardAttribute,
          },
          this.cardAttributeValues.length - 1 !== index,
          isFromConcreteDirect,
        )}
      </React.Fragment>
    ))
  }

  private renderAttributeField = (
    cardAttribute: ICardAttribute,
    shouldAddSeparator: boolean,
    isFromConcreteDirect?: boolean,
  ): JSX.Element => {
    const { fieldId, fieldValue, color } = cardAttribute

    return (
      <div className="row no-flex-children my2 text large">
        <div
          className={classList({
            h16: true,
            'no-fill': !!color,
          })}
          style={{ color }}
        >
          <DeliveryFieldIcon
            fieldId={fieldId}
            isConcreteDirect={isFromConcreteDirect}
          />
        </div>
        <span style={{ color }}>{fieldValue}</span>
        {shouldAddSeparator && <span className="text light mx4">•</span>}
      </div>
    )
  }

  private renderSharedLocation = (
    locationName: string,
    shouldAddSeparator: boolean,
  ): JSX.Element => {
    return (
      <div className="row no-flex-children my2 text large">
        <div className="h16">
          <MapPinIcon size={16} className="delivery-field-icon no-grow" />
        </div>
        <span>{truncateText(locationName, MAX_NAME_LENGTH)}</span>
        {shouldAddSeparator && <span className="text light mx4">•</span>}
      </div>
    )
  }

  private get companyField(): JSX.Element {
    const { titleCardFields, getFieldValueById } = this.cardFieldsStore
    const { shouldUseMockValues } = this.props

    const hasCompany = titleCardFields.some(
      cf => cf.fieldId === FieldIds.COMPANY,
    )
    const hasStatus = titleCardFields.some(cf => cf.fieldId === FieldIds.STATUS)
    const deliveryStatus = getFieldValueById(
      FieldIds.STATUS,
      shouldUseMockValues,
    )

    const companyName = getFieldValueById(FieldIds.COMPANY, shouldUseMockValues)
    const bookingId = getFieldValueById(FieldIds.ID, shouldUseMockValues)
    const shouldShowStatus = hasStatus && !!deliveryStatus

    if (!hasCompany || !companyName) {
      return null
    }

    const { delivery } = this.props

    return (
      <div className="row y-stretch x-between no-flex-children mb4 overflow-hidden">
        <span className="col row delivery-company-title text extra-large bold ellipsis">
          {companyName}
          <span className="col ml5 text light pa2 pt4">({bookingId})</span>
        </span>
        {shouldShowStatus && (
          <DeliveryStatusLabel
            status={delivery.status}
            cancellationReason={delivery.cancellationReason}
            className="col ml8 no-grow"
          />
        )}
      </div>
    )
  }

  private get bookingTimeAndStatusField(): JSX.Element {
    const { titleCardFields, getFieldValueById } = this.cardFieldsStore

    const hasBookingTime = titleCardFields.some(
      cf => cf.fieldId === FieldIds.BOOKING_TIME,
    )

    const bookingTime = getFieldValueById(
      FieldIds.BOOKING_TIME,
      this.props.shouldUseMockValues,
    )
    const shouldShowTime = hasBookingTime && !!bookingTime

    if (!shouldShowTime) {
      return null
    }

    return (
      <div className="row x-between mb4">
        {shouldShowTime && (
          <DeliveryDateLabel
            delivery={this.props.delivery}
            className="text-ellipsis mr4"
          />
        )}
      </div>
    )
  }

  @computed
  private get cardAttributeValues(): ICardAttribute[] {
    return this.cardFieldsStore.getCardAttributeValues(
      this.localShouldUseMockValues,
    )
  }
}
